import { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import isNil from 'lodash/isNil';
import last from 'lodash/last';

import { FILTERS_CACHE_KEY, MAX_CACHED_PAIRS } from 'shared/consts';
import { LocalStorage } from 'tools';
import {
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
  IFilters,
} from 'types/filters';
import { IFilterValue } from 'types/filters/common';
import { EExchange } from 'web3';

import { getProjectFiltersKey } from '../utils';

type IEnteringFilters =
  | { [key in EDexPairFilters]?: { filters: IFilterValue[]; setted: IFilterValue[] } }
  | { [key in ECexPairFilters]?: { filters: IFilterValue[]; setted: IFilterValue[] } }
  | { [key in ESolPairFilters]?: { filters: IFilterValue[]; setted: IFilterValue[] } }
  | { filters: IFilterValue[]; setted: IFilterValue[] };

interface IPayload {
  pairId?: number;
  exchange?: EExchange;
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  projectId?: number;
  projectType?: EProjectFilters;
  filters: IEnteringFilters;
}

export const setupInitialFiltersReducer: CaseReducer<IFilters, PayloadAction<IPayload>> = (
  state,
  { payload },
) => {
  const updatedAt = new Date(Date.now()).toISOString();

  const { pairId, exchange, general, arbitrage, filters, projectId, projectType } = payload;

  // setuping updated filters state
  const updatedState = { ...state };
  if (!isNil(pairId) && exchange) {
    const key = exchange === EExchange.dex ? 'dex' : exchange === EExchange.sol ? 'sol' : 'cex';

    //@ts-ignore
    updatedState[key][pairId] = { updated_at: updatedAt, filters };
  } else if (general) {
    //@ts-ignore
    updatedState[general] = filters;
  } else if (arbitrage) {
    //@ts-ignore
    updatedState.arbitrage[arbitrage] = filters;
  } else if (!isNil(projectId) && projectType) {
    const key = getProjectFiltersKey(projectId, projectType);
    const nextFilters = (filters as { filters: IFilterValue[]; setted: IFilterValue[] }).filters;
    const nextSetted = (filters as { filters: IFilterValue[]; setted: IFilterValue[] }).setted;

    updatedState.project[key] = {
      updated_at: updatedAt,
      filters: nextFilters ?? [],
      setted: nextSetted ?? [],
    };
  }

  state = updatedState;

  // checking pair for filters overflow
  if (!isNil(pairId) && exchange) {
    const key = exchange === EExchange.dex ? 'dex' : exchange === EExchange.sol ? 'sol' : 'cex';

    const pairStates = Object.keys(updatedState[key]).map(el => Number(el));

    if (pairStates.length <= MAX_CACHED_PAIRS) {
      LocalStorage.set(FILTERS_CACHE_KEY, state);
      return;
    }

    const sortedByUpdatedAt = pairStates
      .filter(pairId => !!updatedState[key][pairId]?.updated_at)
      .map(pairId => ({
        pairId,
        updatedAt: updatedState[key][pairId]!.updated_at,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt).valueOf();
        const dateB = new Date(b.updatedAt).valueOf();

        if (dateA > dateB) return -1;
        if (dateB < dateA) return 1;
        return 0;
      });

    state[key][last(sortedByUpdatedAt)!.pairId] = undefined;
  }

  //saving result to LS
  LocalStorage.set(FILTERS_CACHE_KEY, state);
};
